import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { InboxOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Select, Typography, Space } from 'antd';

import {
  WorkOrderRepairPriorityEnum,
  WorkOrderTypeOfServiceEnum,
} from 'stores/Classes/WorkOrderObject';
import CurrentUserStore from 'stores/CurrentUserStore';

import { customFleets } from 'shared';
import { ShopStore, UserStore, WorkOrderStore } from 'stores';
import { Logger } from 'stores/Classes';

const { TextArea } = Input;

const NoFoundContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const { Option } = Select;
const { Text } = Typography;

const fullWidth = { width: '100%' };

const randomWorkOrderNumber = () => {
  // generate a random number between 1 and 1000000
  // and pad it with 0s to make it 6 digits long
  const randomNumber = `000000${Math.floor(Math.random() * 1000000)}`;
  return randomNumber.substring(randomNumber.length - 6);
};

const WorkOrderForm = ({ shopId, childRef, onSave, workOrder, type }) => {
  const [form] = Form.useForm();
  const [currentServiceTaskOnSearch, setCurrentServiceTaskOnSearch] =
    useState('');
  const [currentServiceTask, setCurrentServiceTask] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [initialStatus, setInitialStatus] = useState('');
  const [locations, setLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [location, setLocation] = useState('');

  const serviceTaskSuggestions = _.chain(WorkOrderStore.key32ServiceTasks)
    .map((description, i) => ({ description, i }))
    .sortBy('description')
    .value();

  const demoDescriptionForUps = '- Check the battery\n- Check the power supply';

  useEffect(() => {
    if (childRef) {
      childRef(form);
    }

    if (
      customFleets.ufpFleet.includes(
        ShopStore.currentShop.parentShopId || shopId
      )
    ) {
      setCurrentDescription(demoDescriptionForUps);
    }

    loadShopUsers();
    loadShopWorkOrderStatuses();
    loadShopLocations();
  }, []);

  const loadShopUsers = async () => {
    const fetchedUsers = await UserStore.fetchUsersForTable({
      offset: 0,
      limit: 100,
      filter: null,
    });
    setUsers(fetchedUsers);
  };

  const loadShopWorkOrderStatuses = async () => {
    const fetchedStatuses =
      await WorkOrderStore.getShopWorkOrderStatuses(shopId);
    const defaultStatus = (fetchedStatuses?.data || []).find(
      (status) => status.is_default
    ) ||
      fetchedStatuses?.data[0] || { key: 'open', value: 'Open' };

    setStatuses(
      _.chain(fetchedStatuses)
        .get('data', [])
        .map((status) => ({
          id: status.id,
          key: status.key,
          name: status.description,
          description: status.description,
          color: status.color,
          text: status.description,
          value: status.key,
          is_default: status.is_default,
        }))
        .value()
    );
    setInitialStatus(defaultStatus.key);
  };

  const loadShopLocations = async () => {
    try {
      if (
        !ShopStore.currentShop.id ||
        ShopStore.currentShop.id === '-1' ||
        ShopStore.currentShop.id === -1
      ) {
        setLocations([]);
        return;
      }
      const locations = await ShopStore.getShopLocations(
        ShopStore.currentShop.id
      );
      setLocations(locations);
      setAllLocations(locations);
    } catch (error) {
      Logger.error(error);
    }
  };

  const getCurrentLoggedUser = () => CurrentUserStore.user.id;

  const onSearchServiceTask = (value) => {
    setCurrentServiceTaskOnSearch(value);
  };

  const onSelectServiceTask = (value) => {
    const suggestion = serviceTaskSuggestions.find((s) => s.i === value);
    setCurrentServiceTask(suggestion.description);
  };

  const onChangeDescription = (event) => {
    setCurrentDescription(event.target.value);
  };

  const onClickAddServiceTask = () => {
    const key = serviceTaskSuggestions.length + 1;
    serviceTaskSuggestions.push({
      i: key,
      description: currentServiceTaskOnSearch,
    });
    onSelectServiceTask(key);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(true);
    }
  };

  const filterOption = (input, option) => {
    return option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  const noServiceTaskFound = currentServiceTaskOnSearch ? (
    <NoFoundContainer>
      <Button
        type="link"
        icon={<InboxOutlined />}
        onClick={onClickAddServiceTask}
      >
        Add {currentServiceTaskOnSearch}
      </Button>
    </NoFoundContainer>
  ) : (
    <NoFoundContainer>
      <InboxOutlined style={{ fontSize: '24px' }} />
      <Text level={4}>No Data</Text>
    </NoFoundContainer>
  );

  const initialValues = {
    woInvoiceNumber: workOrder.woInvoiceNumber || randomWorkOrderNumber(),
    woCreatedBy: workOrder.woCreatedBy || getCurrentLoggedUser(),
    woAssignedTo: workOrder.woAssignedTo,
    woStatus: initialStatus,
    woRepairType: type === 'defects' ? 'driver_identified' : 'inspection',
    woRepairPriority: 'major',
    woLocation: '',
  };

  const onSelectLocation = (value) => {
    setLocation(value);

    const [, code] = value.split('-');
    const currentWONumber = form.getFieldValue('woInvoiceNumber') || '';
    const currentParts = String(currentWONumber).split('-');

    const newWONumber = `${code}-${currentParts[1] || currentWONumber}`;

    form.setFieldsValue({
      woInvoiceNumber: newWONumber,
      woLocation: value,
    });
  };

  const onSearchLocation = (value) => {
    const searchStr = value.toLowerCase();
    const filteredLocations = allLocations.filter(
      ({ code, description }) =>
        code.toLowerCase().includes(searchStr) ||
        description.toLowerCase().includes(searchStr)
    );

    setLocations(filteredLocations);
  };

  const renderServiceTaskAndDescription = () => {
    if (type === 'issues' || type === 'defects') {
      return (
        <Row gutter={[8, 24]} style={{ width: '100%' }}>
          {customFleets.ufpFleet.includes(shopId) && (
            <Col span={24}>
              <Form.Item
                label="Attach a Location to this Work Order:"
                name="woLocation"
              >
                <Select
                  value={location}
                  id="location"
                  onChange={onSelectLocation}
                  onSearch={onSearchLocation}
                  showSearch
                  optionFilterProp="children"
                  style={fullWidth}
                >
                  {locations.map((location) => (
                    <Option
                      key={`${location.id}-${location.code}-${location.description}`}
                      value={`${location.id}-${location.code}-${location.description}`}
                    >
                      {location.code} - {location.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          {type === 'issues' && (
            <>
              <Col span={24}>
                <label htmlFor="serviceTask">Service Task:</label>
                <Select
                  value={currentServiceTask}
                  style={{ width: '100%' }}
                  placeholder="Search for a service task"
                  onChange={onSelectServiceTask}
                  onSearch={onSearchServiceTask}
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOption}
                  notFoundContent={noServiceTaskFound}
                >
                  {serviceTaskSuggestions.map((serviceTask, index) => (
                    <Option key={index} value={serviceTask.i}>
                      {serviceTask.description}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={24}>
                <label htmlFor="description">Description:</label>
                <TextArea
                  rows={4}
                  placeholder="Add Description"
                  value={currentDescription}
                  onChange={onChangeDescription}
                />
              </Col>
            </>
          )}
        </Row>
      );
    }
    return null;
  };

  return (
    <Form
      form={form}
      onFinish={handleSave}
      initialValues={initialValues}
      layout="vertical"
    >
      <Text strong>Work Order Details</Text>
      <Row gutter={[8, 24]} style={{ marginTop: '16px' }}>
        <Col span={8}>
          <Form.Item
            label="Work order number:"
            name="woInvoiceNumber"
            rules={[
              { required: true, message: 'Work order number is required!' },
            ]}
          >
            <Input prefix="#" allowClear placeholder="#000000" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Created by:"
            name="woCreatedBy"
            rules={[{ required: true, message: 'Created by is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a User"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {users.map((user, index) => (
                <Option key={index} value={user.userId}>
                  {`${user.firstName} ${user.lastName}`.trim() || user.email}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Assigned To:"
            name="woAssignedTo"
            rules={[{ required: true, message: 'Assigned to is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a User"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {users.map((user, index) => (
                <Option key={index} value={user.userId}>
                  {`${user.firstName} ${user.lastName}`.trim() || user.email}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Status:"
            name="woStatus"
            rules={[{ required: true, message: 'Status is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a Status"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {statuses.map((status) => (
                <Option key={status.key} value={status.key}>
                  {status.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Repair Type:"
            name="woRepairType"
            rules={[{ required: true, message: 'Repair Type is required!' }]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a Repair Type"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
              disabled={type === 'defects'}
            >
              {Object.entries(WorkOrderTypeOfServiceEnum).map(
                ([key, value]) => (
                  <Option key={key} value={key}>
                    {value}
                  </Option>
                )
              )}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Repair Priority:"
            name="woRepairPriority"
            rules={[
              { required: true, message: 'Repair Priority is required!' },
            ]}
          >
            <Select
              style={fullWidth}
              placeholder="Select a Repair Priority"
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {Object.entries(WorkOrderRepairPriorityEnum).map(
                ([key, value]) => (
                  <Option key={key} value={key}>
                    {value}
                  </Option>
                )
              )}
            </Select>
          </Form.Item>
        </Col>
        {renderServiceTaskAndDescription()}
      </Row>
    </Form>
  );
};

WorkOrderForm.propTypes = {
  shopId: PropTypes.number,
  childRef: PropTypes.func,
  onSave: PropTypes.func,
  workOrder: PropTypes.object,
  type: PropTypes.string,
};

export default WorkOrderForm;
